<template>
  <main class="main re">

    <Breadcrumbs :breadcrumbs="breadcrumbs" />

    <!-- Hero -->
    <section class="re-hero">
      <div class="container re-hero__container">
        <div class="re-hero__content">
          <div class="re-hero__label">Недвижимость</div>
          <h1 class="re-hero__title title">
            Недвижимость с характером — для жизни и инвестиций
          </h1>
          <p class="re-hero__desc">
            Мы отбираем объекты по двум критериям — инвестиционная привлекательность
            и атмосфера пространства. Никакой свалки предложений — только то, за чем стоит идти.
          </p>
          <div class="re-hero__actions">
            <button class="btn main-btn" @click="isModalOpen = true">
              Подобрать недвижимость
            </button>
            <a href="#for-developers" class="re-hero__secondary">
              Разместить объект
            </a>
          </div>
        </div>
        <div class="re-hero__image">
          <img
            src="/img/realestate.jpg"
            alt="Недвижимость с характером — для жизни и инвестиций"
            loading="eager"
          />
        </div>
      </div>
    </section>

    <!-- Философия отбора -->
    <section class="re-philosophy">
      <div class="container re-philosophy__container">
        <div class="re-philosophy__text">
          <div class="re-philosophy__label">Философия отбора</div>
          <h2 class="title re-philosophy__title">Мы не агрегатор.<br>Мы редакция.</h2>
          <div class="re-philosophy__body">
            <p>Каждый объект в каталоге Шарунстор проходит через два фильтра.</p>
            <p>
              <strong>Первый — инвестиционная привлекательность.</strong>
              Локация, застройщик, динамика рынка, потенциал роста стоимости и арендного дохода.
            </p>
            <p>
              <strong>Второй — атмосфера.</strong>
              Архитектура, среда вокруг, ощущение от пространства. Потому что недвижимость —
              это не только актив, это место где проходит жизнь.
            </p>
            <p class="re-philosophy__conclusion">
              Если объект не проходит хотя бы один из фильтров — его нет в нашем каталоге.
            </p>
          </div>
        </div>
        <div class="re-philosophy__image">
          <img
            src="/img/real-infoblock.jpg"
            alt="Философия отбора недвижимости"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <!-- Как мы работаем с недвижимостью -->
    <section class="re-how">
      <div class="container">
        <h2 class="title re-how__heading">Как мы работаем с недвижимостью</h2>
        <div class="re-how__grid">

          <div class="re-how__card">
            <div class="re-how__icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
                <path d="M9 12h6M9 16h4"/>
              </svg>
            </div>
            <h3 class="re-how__card-title">Каталог отобранной недвижимости</h3>
            <p class="re-how__card-text">Только объекты от проверенных застройщиков — прошедшие через наш фильтр инвестиционной привлекательности и атмосферности.</p>
          </div>

          <div class="re-how__card">
            <div class="re-how__icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="7"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
            </div>
            <h3 class="re-how__card-title">Поиск и проверка объектов</h3>
            <p class="re-how__card-text">Наши специалисты ищут и проверяют объекты до того, как они попадают в каталог. Вы экономите время и снижаете риски.</p>
          </div>

          <div class="re-how__card">
            <div class="re-how__icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4"/>
                <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01"/>
              </svg>
            </div>
            <h3 class="re-how__card-title">Собственные проекты</h3>
            <p class="re-how__card-text">Выступаем продюсерами жилых и коммерческих объектов. Работа уже ведётся — следите за обновлениями.</p>
          </div>

          <div class="re-how__card">
            <div class="re-how__icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <circle cx="7.5" cy="15.5" r="5.5"/>
                <path d="M21 2l-9.6 9.6M15.5 7.5l2 2"/>
              </svg>
            </div>
            <h3 class="re-how__card-title">Аренда</h3>
            <p class="re-how__card-text">Доходные объекты от операторов и управляющих компаний. Только те, где аренда — это реальный инструмент дохода, а не обещание.</p>
          </div>

        </div>
      </div>
    </section>

    <!-- Объекты в каталоге -->
    <section class="re-catalog">
      <div class="container">
        <h2 class="re-catalog__heading title">Объекты в каталоге</h2>

        <!-- Проекты -->
        <div v-if="projects.length" class="re-catalog__group">
          <div class="re-catalog__group-label">Проекты</div>
          <div class="re-catalog__projects">
            <NuxtLink
              v-for="project in projects"
              :key="project.id"
              :to="`/real-estate/project/${project.id}`"
              class="re-catalog__project-card"
            >
              <div class="re-catalog__project-img">
                <img
                  v-if="project.mainImage"
                  :src="project.mainImage.pathWebp || project.mainImage.path"
                  :alt="project.name"
                  loading="lazy"
                />
                <div v-else class="re-catalog__img-placeholder" />
              </div>
              <div class="re-catalog__project-body">
                <div v-if="project.developerName" class="re-catalog__project-developer">
                  {{ project.developerName }}
                </div>
                <h3 class="re-catalog__project-name">{{ project.name }}</h3>
                <p v-if="project.tagline" class="re-catalog__project-tagline">
                  {{ project.tagline }}
                </p>
              </div>
            </NuxtLink>
          </div>
        </div>

        <!-- Объекты -->
        <div v-if="objects.length" class="re-catalog__group">
          <div class="re-catalog__group-label">Объекты</div>
          <div class="re-catalog__objects">
            <NuxtLink
              v-for="obj in objects"
              :key="obj.id"
              :to="`/real-estate/${obj.id}`"
              class="re-catalog__object-item"
            >
              <div class="re-catalog__object-img">
                <img
                  v-if="obj.mainImage"
                  :src="obj.mainImage.pathWebp || obj.mainImage.path"
                  :alt="obj.name"
                  loading="lazy"
                />
                <div v-else class="re-catalog__img-placeholder re-catalog__img-placeholder--obj" />
              </div>
              <div class="re-catalog__object-body">
                <h3 class="re-catalog__object-name">{{ obj.name }}</h3>
                <p v-if="obj.tagline" class="re-catalog__object-tagline">{{ obj.tagline }}</p>
                <p v-else-if="obj.description" class="re-catalog__object-tagline">
                  {{ obj.description }}
                </p>
              </div>
            </NuxtLink>
          </div>
        </div>

      </div>
    </section>

    <!-- Форма подбора — inline -->
    <SectionFormBuyers />

    <!-- Для застройщиков -->
    <section id="for-developers" class="re-developers">
      <div class="re-developers__bg" aria-hidden="true"></div>
      <div class="container re-developers__inner">

        <div class="re-developers__label">Партнёрам</div>
        <h2 class="title re-developers__title">Разместите объекты и проекты на нашей платформе</h2>
        <p class="re-developers__desc">Мы не просто продаём недвижимость, а формируем отношение к бренду застройщика и его продуктам.</p>

        <div class="re-developers__theses">

          <div class="re-developers__thesis">
            <div class="re-developers__thesis-title">Аудитория с запросом</div>
            <p class="re-developers__thesis-text">Люди, которые уже ищут качественную недвижимость для жизни и инвестиций — не случайный трафик.</p>
          </div>

          <div class="re-developers__thesis">
            <div class="re-developers__thesis-title">Редакционный подход</div>
            <p class="re-developers__thesis-text">Ваш объект подаётся в контексте образа жизни, а не в списке среди тысячи других.</p>
          </div>

          <div class="re-developers__thesis">
            <div class="re-developers__thesis-title">Коллаборации</div>
            <p class="re-developers__thesis-text">Совместный контент, спецпроекты, присутствие в вайбах платформы.</p>
          </div>

        </div>

        <button class="re-developers__cta" @click="isDeveloperModalOpen = true">
          Подать заявку
        </button>

      </div>
    </section>

    <!-- FAQ -->
    <section class="re-faq">
      <div class="container re-faq__container">
        <h2 class="title re-faq__title">Часто задаваемые вопросы</h2>
        <div class="re-faq__list">
          <div
            v-for="(item, i) in faq"
            :key="i"
            class="re-faq__item"
            :class="{ 're-faq__item--open': openFaq === i }"
          >
            <button class="re-faq__question" @click="openFaq = openFaq === i ? null : i">
              <span>{{ item.q }}</span>
              <svg class="re-faq__icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <div class="re-faq__answer">
              <div class="re-faq__answer-inner">{{ item.a }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </main>

  <ClientOnly>
    <teleport to="body">
      <FormRealEstate v-if="isModalOpen" @close="isModalOpen = false" />
      <FormDeveloper v-if="isDeveloperModalOpen" @close="isDeveloperModalOpen = false" />
      <Cookie />
    </teleport>
  </ClientOnly>
</template>

<script setup>
import Breadcrumbs from '~/components/Section/Breadcrumbs.vue'
import FormRealEstate from '~/components/Modals/FormRealEstate.vue'
import FormDeveloper from '~/components/Modals/FormDeveloper.vue'
import SectionFormBuyers from '~/components/Section/FormBuyers.vue'
import Cookie from '~/components/Section/Cookie.vue'

const isModalOpen = ref(false)
const isDeveloperModalOpen = ref(false)
const openFaq = ref(null)

const faq = [
  {
    q: 'Чем вы отличаетесь от обычного агрегатора недвижимости?',
    a: 'Мы не публикуем всё подряд. Каждый объект проходит через два фильтра — инвестиционная привлекательность и атмосфера пространства. Если объект не соответствует — его нет в каталоге.',
  },
  {
    q: 'Как вы зарабатываете?',
    a: 'Комиссия с продаж и аренды — со стороны застройщика или собственника, не с покупателя. Цена объекта для вас не меняется.',
  },
  {
    q: 'С какими локациями вы работаете?',
    a: 'Готовы работать с застройщиками и объектами по всему миру. Сейчас в каталоге объекты из Шереге — география активно расширяется. Если вас интересует конкретная локация — напишите, подберём варианты или сообщим когда появятся.',
  },
  {
    q: 'Как проверяются застройщики?',
    a: 'Мы работаем только с теми, кого знаем лично или кто прошёл нашу проверку — репутация, история объектов, финансовая устойчивость. Анонимных размещений нет.',
  },
  {
    q: 'Можно ли купить объект дистанционно?',
    a: 'Да, сопровождаем сделку удалённо. Менеджер проконсультирует по всем этапам — от выбора до оформления.',
  },
  {
    q: 'Что такое собственные проекты Sharun?',
    a: 'Мы выступаем продюсерами — формируем концепцию, подбираем партнёров и сопровождаем реализацию. Это не типовое жильё, а объекты с характером. Работа уже ведётся.',
  },
  {
    q: 'Я застройщик — как разместить объект?',
    a: 'Оставьте заявку в блоке для застройщиков — менеджер свяжется и расскажет об условиях сотрудничества.',
  },
]

const config = useRuntimeConfig()
const apiBase = config.public.apiDomain

const { data: projectsRes } = await useFetch(`${apiBase}/real-estate/projects/index`, {
  params: { limit: 20 }
})
const projects = computed(() => projectsRes.value?.data?.projects ?? [])

const { data: objectsRes } = await useFetch(`${apiBase}/real-estate/index`, {
  params: { status: 'published', limit: 50 }
})
const objects = computed(() => objectsRes.value?.data?.objects ?? [])

useHead({
  title: 'Недвижимость для инвестиций и жизни | Шарунстор',
  meta: [
    {
      name: 'description',
      content: 'Недвижимость с характером — жилая и инвестиционная. Отбираем объекты по двум критериям: инвестиционная привлекательность и атмосфера пространства.'
    }
  ]
})

const breadcrumbs = [
  { title: 'Главная', url: '/' },
  { title: 'Недвижимость' }
]
</script>

<style scoped lang="scss">
$mobile: 48em;
$tablet: 75em;
$dark: #1B1918;
$grey: #666;

.re-hero {
  padding: 6rem 0 8rem;

  &__container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
    align-items: center;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 4rem;
    }
  }

  &__label {
    font-size: 1.2rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(27, 25, 24, 0.4);
    margin-bottom: 2.4rem;
  }

  &__title {
    font-size: 5rem;
    line-height: 1.12;
    margin-bottom: 2.4rem;
    color: $dark;

    @media (max-width: $tablet) {
      font-size: 4rem;
    }

    @media (max-width: $mobile) {
      font-size: 3.2rem;
    }
  }

  &__desc {
    font-size: 1.8rem;
    line-height: 1.6;
    color: $grey;
    margin-bottom: 3.6rem;
    max-width: 52rem;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 3.2rem;
    flex-wrap: wrap;
  }

  &__secondary {
    font-size: 1.5rem;
    color: $dark;
    text-decoration: underline;
    text-underline-offset: 4px;
    transition: opacity 0.15s;

    &:hover {
      opacity: 0.55;
    }
  }

  &__image {
    img {
      width: 100%;
      height: 58rem;
      object-fit: cover;
      border-radius: 0.4rem;
      display: block;

      @media (max-width: $tablet) {
        height: 42rem;
      }

      @media (max-width: $mobile) {
        height: 28rem;
      }
    }
  }
}

// ─── Философия отбора ─────────────────────────────────────────────────────────
.re-philosophy {
  padding: 10rem 0;

  &__container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8rem;
    align-items: center;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 4.8rem;
    }
  }

  &__label {
    font-size: 1.2rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(27, 25, 24, 0.35);
    margin-bottom: 2.4rem;
  }

  &__title {
    margin-bottom: 3.6rem;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 1.8rem;

    p {
      font-size: 1.7rem;
      line-height: 1.7;
      color: $grey;
    }

    strong {
      color: $dark;
      font-weight: 600;
    }
  }

  &__conclusion {
    font-size: 1.7rem;
    line-height: 1.6;
    color: $dark !important;
    font-style: italic;
    padding-top: 0.8rem;
    border-top: 1px solid rgba(27, 25, 24, 0.1);
    margin-top: 0.4rem;
  }

  &__image {
    @media (max-width: $tablet) {
      order: -1;
    }

    img {
      width: 100%;
      height: 64rem;
      object-fit: cover;
      border-radius: 0.4rem;
      display: block;

      @media (max-width: $tablet) {
        height: 44rem;
      }

      @media (max-width: $mobile) {
        height: 28rem;
      }
    }
  }
}

// ─── Как мы работаем с недвижимостью ─────────────────────────────────────────
.re-how {
  padding: 10rem 0;

  &__heading {
    text-align: center;
    margin-bottom: 6.4rem;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 3.2rem;

    @media (max-width: $tablet) {
      grid-template-columns: repeat(2, 1fr);
      gap: 2.4rem;
    }

    @media (max-width: $mobile) {
      grid-template-columns: repeat(2, 1fr);
      gap: 2rem;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
  }

  &__icon {
    color: $dark;
    line-height: 0;

    svg {
      width: 4rem;
      height: 4rem;

      @media (max-width: $mobile) {
        width: 3.2rem;
        height: 3.2rem;
      }
    }
  }

  &__card-title {
    font-size: 1.7rem;
    font-weight: 700;
    color: $dark;
    line-height: 1.3;
  }

  &__card-text {
    font-size: 1.5rem;
    color: $grey;
    line-height: 1.7;
  }
}

// ─── Объекты в каталоге ───────────────────────────────────────────────────────
.re-catalog {
  padding: 10rem 0 12rem;
  background: #f7f7f7;

  &__heading {
    font-size: 4rem;
    color: $dark;
    margin-bottom: 6.4rem;

    @media (max-width: $mobile) {
      font-size: 3rem;
      margin-bottom: 4.8rem;
    }
  }

  &__group {
    & + & {
      margin-top: 7.2rem;
    }
  }

  &__group-label {
    font-size: 1.2rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(27, 25, 24, 0.35);
    margin-bottom: 2.8rem;
  }

  // ── Проекты ──
  &__projects {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2.4rem;

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
    }
  }

  &__project-card {
    display: block;
    color: inherit;
    text-decoration: none;
    background: #fff;
    border-radius: 6px;
    overflow: hidden;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 6px 24px rgba(27, 25, 24, 0.08);
    }

    &:hover .re-catalog__project-img img {
      transform: scale(1.02);
    }
  }

  &__project-img {
    overflow: hidden;

    img {
      width: 100%;
      aspect-ratio: 16 / 9;
      object-fit: cover;
      display: block;
      transition: transform 0.4s ease;
    }
  }

  &__project-body {
    padding: 2.4rem;
  }

  &__project-developer {
    font-size: 1.2rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(27, 25, 24, 0.4);
    margin-bottom: 0.8rem;
  }

  &__project-name {
    font-size: 2rem;
    font-weight: 700;
    color: $dark;
    line-height: 1.25;
    margin-bottom: 1rem;
  }

  &__project-tagline {
    font-size: 1.5rem;
    color: $grey;
    line-height: 1.6;
  }

  // ── Объекты ──
  &__objects {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem 2.4rem;

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
      gap: 3.2rem;
    }
  }

  &__object-item {
    display: block;
    color: inherit;
    text-decoration: none;

    &:hover .re-catalog__object-img img {
      transform: scale(1.02);
    }

    &:hover .re-catalog__object-name {
      opacity: 0.7;
    }
  }

  &__object-img {
    margin-bottom: 1.6rem;
    overflow: hidden;
    border-radius: 4px;

    img {
      width: 100%;
      aspect-ratio: 4 / 3;
      object-fit: cover;
      display: block;
      transition: transform 0.4s ease;
    }
  }

  &__object-name {
    font-size: 1.8rem;
    font-weight: 700;
    color: $dark;
    line-height: 1.3;
    margin-bottom: 0.8rem;
    transition: opacity 0.2s;
  }

  &__object-tagline {
    font-size: 1.5rem;
    color: $grey;
    line-height: 1.6;
  }

  // ── Заглушка без изображения ──
  &__img-placeholder {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #ececec;
  }
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
.re-faq {
  padding: 10rem 0 12rem;
  background: #f7f7f7;

  &__container {
    max-width: 84rem;
  }

  &__title {
    margin-bottom: 5.6rem;
  }

  &__list {
    border-top: 1px solid rgba(27, 25, 24, 0.12);
  }

  &__item {
    border-bottom: 1px solid rgba(27, 25, 24, 0.12);
  }

  &__question {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    text-align: left;
    padding: 2.4rem 0;
    font-size: 1.7rem;
    font-weight: 500;
    color: $dark;
    line-height: 1.4;
    gap: 2rem;
    cursor: pointer;
    background: none;
    border: none;

    &:hover {
      opacity: 0.7;
    }
  }

  &__icon {
    flex-shrink: 0;
    color: $dark;
    transition: transform 0.3s ease;
  }

  &__item--open &__icon {
    transform: rotate(180deg);
  }

  &__answer {
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.35s ease;
  }

  &__item--open &__answer {
    max-height: 40rem;
  }

  &__answer-inner {
    font-size: 1.6rem;
    line-height: 1.75;
    color: $grey;
    padding-bottom: 2.4rem;
  }
}

// ─── Для застройщиков ─────────────────────────────────────────────────────────
.re-developers {
  position: relative;
  padding: 12rem 0;
  overflow: hidden;

  &__bg {
    position: absolute;
    inset: 0;
    background: url('/img/services/realty/for-developers.jpg') center / cover no-repeat;
    z-index: 0;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(27, 25, 24, 0.8);
    }
  }

  &__inner {
    position: relative;
    z-index: 1;
  }

  &__label {
    font-size: 1.2rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.35);
    margin-bottom: 2.4rem;
  }

  &__title {
    color: #fff;
    font-size: 4.8rem;
    max-width: 72rem;
    margin-bottom: 2.4rem;
    line-height: 1.1;

    @media (max-width: $tablet) {
      font-size: 3.8rem;
    }

    @media (max-width: $mobile) {
      font-size: 3rem;
    }
  }

  &__desc {
    font-size: 1.8rem;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.65;
    max-width: 58rem;
    margin-bottom: 6.4rem;
  }

  &__theses {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4rem;
    margin-bottom: 6.4rem;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 3.2rem;
    }
  }

  &__thesis {
    border-top: 1px solid rgba(255, 255, 255, 0.14);
    padding-top: 2.4rem;
  }

  &__thesis-title {
    font-size: 1.6rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 1.2rem;
  }

  &__thesis-text {
    font-size: 1.5rem;
    color: rgba(255, 255, 255, 0.55);
    line-height: 1.7;
  }

  &__cta {
    display: inline-block;
    padding: 1.6rem 4rem;
    background: #fff;
    color: $dark;
    border: none;
    border-radius: 2px;
    font-size: 1.4rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.88;
    }
  }
}
</style>
